import { useCallback, useEffect, useState } from 'react';

import { getApiLink } from '../api';

const HTML_CONTENT_START_MARKER = '```html';
const HTML_CONTENT_END_MARKER = '```';

interface iUseStreamResult {
	isLoading: boolean;
	htmlContent: string;
	citations: iHtmlCitation[];
	error: Error | null;
	statusCode: number | null;
	isSuccess: boolean;
}

interface iStreamProcessorParams {
	reader: ReadableStreamDefaultReader<Uint8Array>;
	onHtmlContent: (html: string) => void;
	onComplete: (citations: iHtmlCitation[]) => void;
	onError: (error: Error) => void;
}

function getSearchStreamRequest(query: string, controller: AbortController): Promise<Response> {
	return fetch(getApiLink('PublicationClient/SearchStream', { query }), {
		method: 'POST',
		signal: controller.signal,
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			'Accept': 'text/event-stream'
		}
	});
}

export default function useSearchResultsStream(query: string, shouldFetchStream = true): iUseStreamResult {
	const [isLoading, setIsLoading] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [htmlContent, setHtmlContent] = useState('');
	const [citations, setCitations] = useState<iHtmlCitation[]>([]);
	const [error, setError] = useState<Error | null>(null);
	const [statusCode, setStatusCode] = useState<number | null>(null);

	const fetchStream = useCallback(async () => {
		if (!query) {
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		setHtmlContent('');
		setCitations([]);
		setStatusCode(null);
		setError(null);
		setIsSuccess(false);

		const controller = new AbortController();

		try {
			const response = await getSearchStreamRequest(query, controller);
			setStatusCode(response.status);

			if (!response.ok) {
				throw new Error('Stream API Error');
			}

			const reader = response.body?.getReader();
			if (!reader) {
				throw new Error('Stream reader could not be created');
			}

			await processStreamData({
				reader,
				onHtmlContent: (html) => setHtmlContent(html),
				onComplete: (citations) => {
					setCitations(citations);
					setIsLoading(false);
					setIsSuccess(true);
				},
				onError: (err) => {
					setError(err);
					setIsSuccess(false);
					setIsLoading(false);
				}
			});
		} catch (err) {
			setError(err instanceof Error ? err : new Error(String(err)));
			setIsLoading(false);
		}

		return () => {
			controller.abort();
		};
	}, [query]);

	useEffect(() => {
		const abortController = { abort: () => { } };

		if (shouldFetchStream) {
			fetchStream()
			.then(cleanup => {
				if (cleanup) {
					abortController.abort = cleanup;
				}
			})
			.catch(err => {
				console.error('Error starting stream:', err);
			});
		}

		return () => {
			abortController.abort();
		};
	}, [fetchStream, shouldFetchStream]);

	return {
		isLoading,
		htmlContent,
		citations,
		error,
		statusCode,
		isSuccess
	};
}

/**
 * Extracts HTML content from a raw response string that contains 
 * markdown-formatted HTML code blocks. If the response doesn't contain
 * the HTML marker but is longer than the marker length, treats the entire
 * response as HTML content.
 */
function extractHtmlContent(rawResponse: string): string {
	// If response is empty, return empty string
	if (!rawResponse) return '';

	// Extract content between start and end markers
	const htmlStartIndex = rawResponse.indexOf(HTML_CONTENT_START_MARKER);
	
	// If no start marker found and response is longer than marker length,
	// treat entire response as HTML content
	if (htmlStartIndex === -1) {
		return rawResponse.length > HTML_CONTENT_START_MARKER.length 
			? repairBrokenHtmlTags(rawResponse)
			: '';
	}

	const contentStartIndex = htmlStartIndex + HTML_CONTENT_START_MARKER.length;
	let htmlContent = rawResponse.substring(contentStartIndex);

	const closingBackticksIndex = htmlContent.indexOf(HTML_CONTENT_END_MARKER);
	if (closingBackticksIndex > -1) {
		htmlContent = htmlContent.substring(0, closingBackticksIndex);
	}

	return repairBrokenHtmlTags(htmlContent);
}

interface iHtmlCitation {
	id: number;
	pages: string[];
}

/**
 * Extracts all <cite> tags from HTML content string by parsing it into a DOM structure
 * and using querySelector to find them. Groups citations by ID and collects all pages.
 * @param htmlContent The HTML content string to extract citations from
 * @returns Array of citation objects with id and pages
 */
function extractCitationsFromHtml(htmlContent: string): iHtmlCitation[] {
	if (!htmlContent) return [];
	
	try {
		const parser = new DOMParser();
		const doc = parser.parseFromString(htmlContent, 'text/html');
		
		const citeElements = doc.querySelectorAll('cite');
		
		const citationsMap = new Map<string, Set<string>>();
		
		Array.from(citeElements).forEach(cite => {
			const citationId = cite.getAttribute('data-citation-id');
			if (!citationId) return;
			
			const citeText = cite.textContent || '';
			const pageMatch = citeText.match(/Page (\d+)/i);
			const page = pageMatch ? pageMatch[1] : '';
			
			if (!citationsMap.has(citationId)) {
				citationsMap.set(citationId, new Set<string>());
			}
			
			if (page) {
				citationsMap.get(citationId)?.add(page);
			}
		});
		
		return Array.from(citationsMap.entries()).map(([id, pagesSet]) => ({
			id: parseInt(id, 10),
			pages: Array.from(pagesSet)
		}));
	} catch (error) {
		console.error('Error parsing HTML to extract citations:', error);
		return [];
	}
}

async function processStreamData({
	reader,
	onHtmlContent,
	onComplete,
	onError
}: iStreamProcessorParams): Promise<void> {
	let rawResponse = '';

	try {
		while (true) {
			const { done, value } = await reader.read();

			if (done) {
				const finalHtmlContent = extractHtmlContent(rawResponse);
				const citations = extractCitationsFromHtml(finalHtmlContent);
				onComplete(citations);
				break;
			}

			const chunk = new TextDecoder().decode(value);
			rawResponse += chunk;

			onHtmlContent(extractHtmlContent(rawResponse));
		}
	} catch (err) {
		if (err instanceof Error && err.name === 'AbortError') {
			return;
		}
		onError(err instanceof Error ? err : new Error(String(err)));
	}
}

/**
 * Repairs broken HTML tags in stream content that might have been split across chunks.
 * Handles malformed opening tags, closing tags, and self-closing tags by removing
 * extra whitespace and ensuring proper tag syntax.
 */
function repairBrokenHtmlTags(htmlContent: string): string {
	// Fix broken opening tags (< tag)
	htmlContent = htmlContent.replace(/\s*<\s+(\w+)/g, '<$1');

	// Fix broken closing tags (</ tag)
	htmlContent = htmlContent.replace(/\s*<\/\s+(\w+)/g, '</$1');

	// Fix broken self-closing tags (< img/)
	htmlContent = htmlContent.replace(/\s*<\s+(\w+)\s*\/>/g, '<$1/>');

	return htmlContent;
}